// Pure, framework-free utilities for the organization chart (line styles + geometry).
//
// Tipenya datang dari model generated (./api), bukan dari salinan tulis-tangan di ./types.
// Salinan itu sempat ada dan berlabel "mirrors desakita ChartData DTO" — cermin yang tidak
// dijaga apa pun: bentuk kontrak bisa berubah tanpa satu pun berkas di sini ikut merah.
// `JenisGaris` lama kini bernama `LineType`, nama yang dipakai kontraknya sendiri.
import type { ChartEdge, ChartNode, LineType, ModeGaris } from './api';

export const DEFAULT_NODE_WIDTH = 200;
export const DEFAULT_NODE_HEIGHT = 120;
const STUB = 24;
const CANVAS_PADDING = 40;

export interface GarisStyle { warna: string; ketebalan: number; dash?: number; gap?: number; }

export const GARIS_DEFAULT: Record<LineType, GarisStyle> = {
  KOMANDO:     { warna: '#1f2937', ketebalan: 2 },
  KOORDINASI:  { warna: '#2563eb', ketebalan: 2, dash: 8, gap: 6 },
  KONSULTATIF: { warna: '#7c3aed', ketebalan: 2, gap: 6 },
  FUNGSIONAL:  { warna: '#059669', ketebalan: 2, dash: 12, gap: 6 },
};

export const dashArrayFor = (jenis: LineType): string => {
  const style = GARIS_DEFAULT[jenis];
  const t = Math.max(1, style.ketebalan);
  switch (jenis) {
    case 'KOMANDO': return '0';
    case 'KOORDINASI': return `${style.dash || 8} ${style.gap || 6}`;
    case 'KONSULTATIF': return `${t} ${(style.gap || 6) + 2}`;
    case 'FUNGSIONAL': return `${style.dash || 12} ${style.gap || 6} ${t} ${style.gap || 6}`;
    default: return '0';
  }
};

export const LABEL_JENIS: Record<LineType, string> = {
  KOMANDO: 'Komando', KOORDINASI: 'Koordinasi', KONSULTATIF: 'Konsultatif', FUNGSIONAL: 'Fungsional',
};

export const URUTAN_JENIS: LineType[] = ['KOMANDO', 'KOORDINASI', 'KONSULTATIF', 'FUNGSIONAL'];

export interface NodeLayout { x: number; y: number; w: number; h: number; color: string | null; }

export const resolveNodeLayout = (n: ChartNode): NodeLayout => ({
  x: n.posX ?? 0,
  y: n.posY ?? 0,
  w: n.nodeWidth ?? DEFAULT_NODE_WIDTH,
  h: n.nodeLength ?? DEFAULT_NODE_HEIGHT,
  color: n.nodeColor ?? null,
});

export const buildLayoutMap = (nodes: ChartNode[]): Record<string, NodeLayout> => {
  const out: Record<string, NodeLayout> = {};
  nodes.forEach(n => { out[n.id] = resolveNodeLayout(n); });
  return out;
};

export interface CanvasBounds { w: number; h: number; }

export const computeBounds = (layout: Record<string, NodeLayout>): CanvasBounds => {
  let maxX = 0, maxY = 0;
  Object.values(layout).forEach(L => {
    maxX = Math.max(maxX, L.x + L.w);
    maxY = Math.max(maxY, L.y + L.h);
  });
  return { w: maxX + CANVAS_PADDING, h: maxY + CANVAS_PADDING };
};

// A chart is "configured" once at least one node has a saved position.
export const hasConfiguredLayout = (nodes: ChartNode[]): boolean =>
  nodes.some(n => n.posX != null && n.posY != null);

// Detects the village-head row. The default seeded head jabatan is named "Pimpinan"
// (its alternative title is "Kepala Desa"), so match both labels.
export const isKepalaDesa = (nama?: string | null): boolean => {
  if (!nama) return false;
  const n = nama.toLowerCase();
  return n.includes('kepala desa') || n.includes('pimpinan');
};

// The chart's structural root: the node that is never the target of a KOMANDO edge
// (nobody commands it). Falls back to a name-matched "kepala desa", then the topmost
// node by posY, then the first node. Robust to non-standard head titles.
export const findRootDetailId = (nodes: ChartNode[], edges: ChartEdge[]): string | null => {
  if (!nodes.length) return null;
  const komandoTargets = new Set(
    edges.filter(e => e.lineType === 'KOMANDO').map(e => e.targetDetailId)
  );
  const pool = nodes.filter(n => !komandoTargets.has(n.id));
  const roots = pool.length ? pool : nodes;
  const named = roots.find(n => isKepalaDesa(n.namaJabatan));
  if (named) return named.id;
  return [...roots].sort((a, b) => (a.posY ?? 0) - (b.posY ?? 0))[0].id;
};

interface Anchors { sx: number; sy: number; tx: number; ty: number; }

const getAnchors = (src: NodeLayout, tgt: NodeLayout): Anchors => ({
  sx: src.x + src.w / 2, sy: src.y + src.h / 2,
  tx: tgt.x + tgt.w / 2, ty: tgt.y + tgt.h / 2,
});

// SVG path between two node layouts. Read-only port of the desakita canvas:
// LURUS = straight; SIKU = vertical-first elbow with optional bend (lineBendX/Y).
export const edgePath = (
  src: NodeLayout, tgt: NodeLayout, mode: ModeGaris,
  bendX: number | null, bendY: number | null
): string => {
  const { sx, sy, tx, ty } = getAnchors(src, tgt);
  if (mode === 'LURUS') return `M ${sx} ${sy} L ${tx} ${ty}`;
  const isDown = ty >= sy;
  const midY = bendY ?? (sy + ty) / 2;
  if (bendX != null) {
    const Y1 = bendY ?? (isDown ? src.y + src.h + STUB : src.y - STUB);
    const Y2 = isDown ? tgt.y - STUB : tgt.y + tgt.h + STUB;
    return [
      `M ${sx} ${sy}`, `L ${sx} ${Y1}`, `L ${bendX} ${Y1}`,
      `L ${bendX} ${Y2}`, `L ${tx} ${Y2}`, `L ${tx} ${ty}`,
    ].join(' ');
  }
  return `M ${sx} ${sy} L ${sx} ${midY} L ${tx} ${midY} L ${tx} ${ty}`;
};
